import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import api from "@/lib/api";
import { Lock, ArrowRight, X } from "lucide-react";

const BG =
  "https://images.unsplash.com/photo-1667400104789-f50a4cb393cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwbWFyYmxlJTIwYXJ0fGVufDB8fHx8MTc4NjU2NzgwMHww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState("");
  const [monthCode, setMonthCode] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const pwRef = useRef(null);

  useEffect(() => {
    if (user) navigate("/portal", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (step === 2) setTimeout(() => pwRef.current?.focus(), 350);
  }, [step]);

  const openGate = (e) => {
    e.preventDefault();
    if (!username.trim()) return;
    setError("");
    setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(username.trim(), password, monthCode);
      navigate("/portal", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setStep(1);
    setPassword("");
    setMonthCode("");
    setError("");
    setInfo("");
  };

  const forgot = async () => {
    setError("");
    setInfo("");
    if (!monthCode) {
      setError("Enter the access code (month) first to verify it's you.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", {
        username: username.trim(),
        month_code: monthCode,
      });
      setPassword("");
      setInfo("Password reset to the initial password. Enter it below to sign in.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#F8F9FA]">
      <img
        src={BG}
        alt=""
        className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover opacity-40 blur-3xl"
      />
      <div className="noise-overlay" />

      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-6">
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.form
              key="step1"
              onSubmit={openGate}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20, filter: "blur(6px)" }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="flex w-full max-w-2xl flex-col items-center text-center"
            >
              <span className="mb-10 text-[11px] uppercase tracking-[0.55em] text-slate-400 font-plex">
                Nexus
              </span>
              <input
                data-testid="username-input"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="who goes there"
                className="ghost-input font-display"
              />
              <div className="mt-8 flex items-center gap-2 text-slate-300 transition-colors">
                <span className="text-xs tracking-[0.3em] uppercase font-plex">
                  press enter
                </span>
                <ArrowRight className="h-3.5 w-3.5" />
              </div>
            </motion.form>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {step === 2 && (
            <>
              <motion.div
                className="absolute inset-0 z-20 bg-slate-900/10 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={close}
              />
              <motion.form
                key="step2"
                onSubmit={submit}
                initial={{ opacity: 0, scale: 0.95, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-30 w-full max-w-md rounded-3xl border border-white/60 bg-white/70 p-12 shadow-[0_20px_80px_rgba(15,23,42,0.12)] backdrop-blur-2xl"
              >
                <button
                  type="button"
                  onClick={close}
                  data-testid="close-gate-btn"
                  className="absolute right-5 top-5 text-slate-300 hover:text-slate-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="mb-8 flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-900 text-white">
                    <Lock className="h-4 w-4" />
                  </div>
                  <div className="text-left">
                    <p className="font-display text-xl leading-none text-slate-900">
                      Safewall
                    </p>
                    <p className="mt-1 font-plex text-xs text-slate-400">
                      Welcome, {username}
                    </p>
                  </div>
                </div>

                <label className="mb-1 block font-plex text-[11px] uppercase tracking-[0.25em] text-slate-400">
                  Password
                </label>
                <input
                  ref={pwRef}
                  data-testid="password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="line-input font-plex mb-7"
                  placeholder="••••••••"
                />

                <label className="mb-1 block font-plex text-[11px] uppercase tracking-[0.25em] text-slate-400">
                  Access code · month (01–12)
                </label>
                <input
                  data-testid="month-input"
                  inputMode="numeric"
                  maxLength={2}
                  value={monthCode}
                  onChange={(e) =>
                    setMonthCode(e.target.value.replace(/[^0-9]/g, ""))
                  }
                  className="line-input font-plex tracking-[0.4em]"
                  placeholder="MM"
                />

                {error && (
                  <p
                    data-testid="login-error"
                    className="mt-5 font-plex text-sm text-rose-500"
                  >
                    {error}
                  </p>
                )}

                {info && (
                  <p
                    data-testid="login-info"
                    className="mt-5 font-plex text-sm text-emerald-600"
                  >
                    {info}
                  </p>
                )}

                <button
                  type="submit"
                  data-testid="unlock-btn"
                  disabled={busy}
                  className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 py-3.5 font-plex text-sm font-medium tracking-wide text-white transition-transform duration-200 hover:-translate-y-0.5 hover:bg-slate-800 disabled:opacity-50"
                >
                  {busy ? "Verifying…" : "Enter portal"}
                  {!busy && <ArrowRight className="h-4 w-4" />}
                </button>

                <button
                  type="button"
                  data-testid="forgot-password-btn"
                  onClick={forgot}
                  disabled={busy}
                  className="mt-4 w-full text-center font-plex text-xs tracking-wide text-slate-400 underline-offset-4 transition-colors hover:text-slate-700 hover:underline disabled:opacity-50"
                >
                  Forgot password?
                </button>
              </motion.form>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
